'use client';

import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { ServiceBadge } from './ServiceBadge';
import { Calculator, MapPin, Package, Box, ArrowRight, Building2, Truck } from 'lucide-react';
import styles from './OnlineServiceCard.module.css';

const iconMap = {
    calculator: Calculator,
    mapPin: MapPin,
    package: Package,
    box: Box,
    arrowRight: ArrowRight,
    building2: Building2,
    truck: Truck,
};

interface OnlineServicesSectionProps {
    icon: keyof typeof iconMap;
    title?: string;
    description: string;
    buttonText: string;
    actionIcon: keyof typeof iconMap;
    badge: string;
    microText: string;
    link: string;
    animation: string;
    delay?: number;
}

export const OnlineServiceCard = ({
    icon,
    title,
    description,
    buttonText,
    actionIcon,
    badge,
    microText,
    link,
    delay,
}: OnlineServicesSectionProps) => {
    const IconComponent = iconMap[icon];
    const ActionIconComponent = iconMap[actionIcon];

    const isNew = badge?.toLowerCase() === 'новинка';
    const cardClasses = [styles.card, isNew ? styles.newCard : styles.defaultCard].join(' ');
    const iconClasses = [styles.iconContainer, isNew ? styles.newIcon : styles.defaultIcon].join(
        ' ',
    );
    const buttonClasses = [styles.button, isNew ? styles.newButton : styles.defaultButton].join(
        ' ',
    );
    const topBorderClasses = [
        styles.topBorder,
        isNew ? styles.newTopBorder : styles.defaultTopBorder,
    ].join(' ');
    const sideBorderClasses = [
        styles.sideBorder,
        isNew ? styles.newSideBorder : styles.defaultSideBorder,
    ].join(' ');

    return (
        <motion.div
            className={cardClasses}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay }}
            whileHover={{
                y: -4,
                boxShadow: '0 12px 24px rgba(0, 0, 0, 0.06)',
                transition: { duration: 0.2 },
            }}
        >
            <div className={styles.cardContent}>
                <div className={styles.header}>
                    <motion.div
                        className={iconClasses}
                        whileHover={{ scale: 1.05 }}
                        transition={{ type: 'spring', stiffness: 400 }}
                    >
                        <IconComponent
                            className={isNew ? styles.newIconColor : styles.defaultIconColor}
                        />
                    </motion.div>

                    {badge && <ServiceBadge text={badge} />}
                </div>

                <h3 className={styles.title}>{title}</h3>
                <p className={styles.description}>{description}</p>

                <Link href={link} className={styles.link}>
                    <motion.button
                        className={buttonClasses}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <span className={styles.buttonText}>{buttonText}</span>
                        <motion.div className={styles.buttonIcon} whileHover={{ x: 4 }}>
                            <ActionIconComponent className={styles.actionIcon} />
                        </motion.div>
                    </motion.button>
                </Link>

                <div className={styles.microText}>{microText}</div>

                <div className={topBorderClasses} />
                <div className={sideBorderClasses} />
            </div>
        </motion.div>
    );
};
